import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  useWindowDimensions,
} from 'react-native';
import { LoadingSpinner } from '../components/common/LoadingSpinner';
import { useAuth } from '../context/AuthContext';
import { timetableAPI } from '../services/api';
import { Logger } from '../utils/logger';
import { WebPageShell } from '../components/common/WebPageShell';
import { useTheme, ThemeColors } from '../context/ThemeContext';

interface Period {
  id: string;
  periodNo: number;
  subject: string;
  teacher: string;
  startTime: string;
  endTime: string;
  room: string;
  isBreak?: boolean;
}

type DayName = 'Mon' | 'Tue' | 'Wed' | 'Thu' | 'Fri' | 'Sat';
type Timetable = Partial<Record<DayName, Period[]>>;

const DAYS: DayName[] = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const DAY_OF_WEEK_TO_NAME: Record<number, DayName> = {
  1: 'Mon',
  2: 'Tue',
  3: 'Wed',
  4: 'Thu',
  5: 'Fri',
  6: 'Sat',
};

const todayIndex = () => {
  const day = new Date().getDay(); // 0=Sun, 1=Mon...
  return day === 0 ? 0 : day - 1; // clamp Sun to Mon
};

const toIsoDate = (d: Date) => {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

function mapSlot(slot: any, index: number): Period {
  const isBreak = slot.isTeachingSlot === false;
  const name: string = isBreak
    ? (slot.slotName ?? 'BREAK').toUpperCase()
    : (slot.subjectName ?? slot.slotName ?? '');
  return {
    id: slot.timeTableId ?? slot.hourlyScheduleId ?? String(index),
    periodNo: isBreak ? 0 : slot.sequenceOrder ?? index,
    subject: name,
    teacher: slot.teacherName ?? '',
    startTime: slot.startTime ?? '',
    endTime: slot.endTime ?? '',
    room: '',
    isBreak,
  };
}

// "08:05" / "08:05:00" -> minutes since midnight; unparseable times sort last.
const toMinutes = (time?: string) => {
  const match = /^(\d{1,2}):(\d{2})/.exec(time ?? '');
  return match ? Number(match[1]) * 60 + Number(match[2]) : Number.MAX_SAFE_INTEGER;
};

function mapTimetable(days: any[]): Timetable {
  const result: Timetable = {};
  (days ?? []).forEach(day => {
    const dayName = DAY_OF_WEEK_TO_NAME[day.dayOfWeek];
    if (!dayName) return;
    result[dayName] = (day.slots ?? [])
      .slice()
      // sequenceOrder is not reliably chronological, so the clock decides.
      .sort((a: any, b: any) =>
        toMinutes(a.startTime) - toMinutes(b.startTime)
        || (a.sequenceOrder ?? 0) - (b.sequenceOrder ?? 0))
      .map(mapSlot);
  });
  return result;
}

export function TimetableScreen({ isNested }: { isNested?: boolean } = {}) {
  const { selectedStudent } = useAuth();
  const { colors } = useTheme();
  const { width } = useWindowDimensions();
  const isMobile = width < 768;
  const styles = useMemo(() => createStyles(colors), [colors]);
  const [loading, setLoading] = useState(true);
  const [timetable, setTimetable] = useState<Timetable | null>(null);
  const [selectedDay, setSelectedDay] = useState<DayName>(DAYS[Math.min(todayIndex(), DAYS.length - 1)]);

  const loadData = useCallback(async () => {
    if (!selectedStudent?.classId) {
      setLoading(false);
      return;
    }
    Logger.info('TimetableWeb', `Loading timetable for ${selectedStudent?.id}`);
    try {
      const { data } = await timetableAPI.getTimetable(
        selectedStudent.classId,
        toIsoDate(new Date()),
        'weekly',
      );
      const daysArray = data?.days ?? [];
      Logger.info('TimetableWeb', `Loaded ${daysArray.length} days`);
      setTimetable(mapTimetable(daysArray));
    } catch (err) {
      Logger.error('TimetableWeb', 'Load failed', err);
    } finally {
      setLoading(false);
    }
  }, [selectedStudent]);

  useEffect(() => { loadData(); }, [loadData]);

  const periods = (timetable ? timetable[selectedDay] : undefined) ?? [];

  const innerContent = loading ? (
    <LoadingSpinner message="Loading timetable..." />
  ) : (
    <View style={[styles.container, isNested && { backgroundColor: 'transparent' }]}>
      <View style={styles.dayBar}>
        <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={styles.dayBarContent}>
          {DAYS.map((day, idx) => {
            const isSelected = day === selectedDay;

            // Get actual date for this day of the current week (starting Monday)
            // If today is Sunday (0), we want to show the upcoming week starting tomorrow
            const dateObj = new Date();
            const currentDay = dateObj.getDay();
            const diff = dateObj.getDate() - currentDay + (currentDay === 0 ? 1 : 1) + idx;
            dateObj.setDate(diff);
            const dateNum = dateObj.getDate();

            return (
              <TouchableOpacity
                key={day}
                style={[styles.dayBtn, isSelected && styles.dayBtnActive]}
                onPress={() => setSelectedDay(day)}>
                <Text style={[styles.dayTextDate, isSelected && styles.dayTextActive]}>{dateNum}</Text>
                <Text style={[styles.dayTextName, isSelected && styles.dayTextActive]}>{day}</Text>
              </TouchableOpacity>
            );
          })}
        </ScrollView>
      </View>

      <View style={styles.scroll}>
        {periods.length === 0 ? (
          <View style={[styles.emptyContainer, isMobile && styles.emptyContainerMobile]}>
            <Text style={styles.emptyText}>No classes scheduled for {selectedDay}.</Text>
          </View>
        ) : (
          <View style={[styles.timelineContainer, isMobile && styles.timelineContainerMobile]}>
            {periods.map((period, index) => {
              const isBreak = period.isBreak;
              const isLast = index === periods.length - 1;

              return (
                <View key={period.id} style={[styles.timelineRow, !isLast && styles.timelineRowBorder]}>
                  <View style={[styles.timeColumn, isMobile && styles.timeColumnMobile]}>
                    <Text style={styles.timeText}>{period.startTime}</Text>
                    {!!period.endTime && <Text style={styles.endTimeText}>{period.endTime}</Text>}
                  </View>

                  <View style={styles.nodeColumn}>
                    <View style={[styles.nodeCircle, isBreak && styles.nodeCircleBreak]} />
                  </View>

                  <View style={[styles.contentColumn, isBreak && styles.contentColumnBreak]}>
                    {isBreak ? (
                      <Text style={styles.breakText}>{period.subject}</Text>
                    ) : (
                      <>
                        <Text style={styles.subjectText}>{period.subject}</Text>
                        {!!(period.teacher || period.room) && (
                          <Text style={styles.roomText}>{period.teacher || period.room}</Text>
                        )}
                      </>
                    )}
                  </View>
                </View>
              );
            })}
          </View>
        )}
        <View style={{ height: 32 }} />
      </View>
    </View>
  );

  if (isNested) {
    return innerContent;
  }

  return <WebPageShell title="Timetable" maxWidth={900}>{innerContent}</WebPageShell>;
}

const createStyles = (colors: ThemeColors) => StyleSheet.create({
  container: { flexGrow: 1, backgroundColor: colors.background, width: '100%' },
  centered: { flex: 1, justifyContent: 'center', alignItems: 'center', backgroundColor: 'transparent' },
  loadingText: { marginTop: 12, color: colors.textSecondary, fontSize: 14 },
  scroll: { flexGrow: 1 },

  dayBar: {
    backgroundColor: 'transparent',
    paddingVertical: 16,
  },
  dayBarContent: {
    paddingHorizontal: 16,
    gap: 12
  },
  dayBtn: {
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderRadius: 16,
    backgroundColor: colors.card,
    alignItems: 'center',
    minWidth: 64,
  },
  dayBtnActive: {
    backgroundColor: colors.primary,
  },
  dayTextDate: {
    fontSize: 16,
    fontWeight: '700',
    color: colors.textSecondary,
    marginBottom: 4,
  },
  dayTextName: {
    fontSize: 12,
    fontWeight: '600',
    color: colors.textMuted
  },
  dayTextActive: {
    color: '#FFFFFF'
  },

  timelineContainer: {
    backgroundColor: colors.card,
    borderRadius: 16,
    marginHorizontal: 16,
    paddingHorizontal: 16,
    paddingTop: 16,
    paddingBottom: 8,
    marginBottom: 24,
  },
  timelineContainerMobile: {
    marginHorizontal: 8,
    paddingHorizontal: 12,
  },
  emptyContainer: {
    backgroundColor: colors.card,
    borderRadius: 16,
    marginHorizontal: 16,
    padding: 32,
    alignItems: 'center',
    justifyContent: 'center',
  },
  emptyContainerMobile: {
    marginHorizontal: 8,
    padding: 20,
  },
  emptyText: {
    color: colors.textSecondary,
    fontSize: 15,
    fontWeight: '500',
    textAlign: 'center',
  },
  timelineRowBorder: {
    borderBottomWidth: 1,
    borderBottomColor: colors.divider,
  },
  timelineRow: {
    flexDirection: 'row',
    paddingVertical: 16,
  },
  timeColumn: {
    width: 50,
  },
  timeColumnMobile: {
    width: 40,
  },
  timeText: {
    fontSize: 14,
    fontWeight: '600',
    color: colors.textMuted,
  },
  endTimeText: {
    fontSize: 12,
    fontWeight: '500',
    color: colors.textMuted,
    marginTop: 2,
  },
  nodeColumn: {
    width: 30,
    alignItems: 'center',
    paddingTop: 2,
  },
  nodeCircle: {
    width: 10,
    height: 10,
    borderRadius: 5,
    backgroundColor: colors.primary,
    marginTop: 4,
  },
  nodeCircleBreak: {
    backgroundColor: colors.textMuted,
  },
  contentColumn: {
    flex: 1,
    paddingLeft: 16,
  },
  contentColumnBreak: {
    justifyContent: 'flex-start',
  },
  subjectText: {
    fontSize: 15,
    fontWeight: '700',
    color: colors.textPrimary,
    marginBottom: 4,
  },
  roomText: {
    fontSize: 13,
    fontWeight: '500',
    color: colors.textMuted,
  },
  breakText: {
    fontSize: 15,
    fontWeight: '600',
    color: colors.textPrimary,
  }
});
